/**
 * SettingsPage - Application settings and integrations management
 *
 * Features:
 * - Theme toggle (light/dark)
 * - Organization management (name, team members)
 * - Integration status and management
 * - Real API integration
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sun, Moon, Github, Trash2, UserPlus, Pencil } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { integrationsApi } from '../lib/integrations-api';
import { usersApi } from '../lib/users-api';
import { LoadingState, ConfirmDialog, Toast, Modal } from '../components/common';
import { JiraLogo } from '../components/icons';
import { useIntegrationStatuses } from '../hooks/useIntegrationStatuses';
import { TENANT_MEMBERS_QUERY_KEY, useTenantMembers } from '../hooks/useTenantMembers';

const getMemberDisplayName = (name: string | null, email: string) => name?.trim() || email;

const getInitials = (name: string | null, email: string) => {
  const source = getMemberDisplayName(name, email);
  const parts = source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return 'U';
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const {
    githubStatus,
    jiraStatus,
    isLoading,
    setConnectedStatus,
    invalidateIntegrationStatuses,
  } = useIntegrationStatuses();
  const {
    members: teamMembers,
    isLoading: isMembersLoading,
    isError: hasMembersError,
  } = useTenantMembers();
  const [organizationName, setOrganizationName] = useState('');

  // Modal states
  const [showGitHubDisconnectConfirm, setShowGitHubDisconnectConfirm] = useState(false);
  const [showJiraDisconnectConfirm, setShowJiraDisconnectConfirm] = useState(false);
  const [showRemoveUserConfirm, setShowRemoveUserConfirm] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [removingUser, setRemovingUser] = useState<{ id: string; name: string } | null>(null);
  const [editingMember, setEditingMember] = useState<{
    id: string;
    name: string;
    role: 'admin' | 'member';
  } | null>(null);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'member'>('member');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [editRoleValue, setEditRoleValue] = useState<'admin' | 'member'>('member');
  const [editRoleError, setEditRoleError] = useState<string | null>(null);

  // Toast states
  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    message: '',
    variant: 'info',
  });

  const showToast = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({ isOpen: true, message, variant });
  };

  // Handle redirect params (e.g., ?github=success, ?jira=success, or ?error=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const githubSuccess = params.get('github');
    const jiraSuccess = params.get('jira');
    const error = params.get('error');
    const errorMessage = params.get('message'); // Additional error details from OAuth

    if (githubSuccess === 'success') {
      setConnectedStatus('github', true);
      invalidateIntegrationStatuses();
      showToast('GitHub connected successfully', 'success');
    }

    if (jiraSuccess === 'success') {
      setConnectedStatus('jira', true);
      invalidateIntegrationStatuses();
      showToast('Jira connected successfully', 'success');
    }

    if (error) {
      const errorMessages: Record<string, string> = {
        // GitHub errors
        installation_failed: 'Failed to connect GitHub. Please try again.',
        missing_installation_id: 'GitHub installation was not completed.',
        // Jira errors
        oauth_denied: errorMessage ? `Authorization denied: ${errorMessage}` : 'Authorization was denied.',
        oauth_failed: 'OAuth connection failed. Please try again.',
        missing_code: 'Authorization code missing. Please try again.',
        // Shared errors
        invalid_state: 'Invalid request. Please try again.',
      };
      showToast(errorMessages[error] || `Connection failed: ${error}`, 'error');
    }

    // Clear params from URL after processing
    if (githubSuccess || jiraSuccess || error) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [invalidateIntegrationStatuses, setConnectedStatus]);

  useEffect(() => {
    setOrganizationName(user?.tenantName ?? '');
  }, [user?.tenantName]);

  const isAdmin = user?.role === 'admin';

  const handleGitHubConnect = async () => {
    try {
      // Use clean URL without query params to avoid duplicates on redirect
      const returnUrl = window.location.origin + window.location.pathname;
      const response = await integrationsApi.getGitHubInstallUrl(returnUrl);

      if (response.reconnected && response.redirectUrl) {
        // Auto-reconnected! Redirect to success URL
        window.location.href = response.redirectUrl;
      } else if (response.url) {
        // Normal flow - redirect to GitHub
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to get GitHub install URL:', error);
      showToast('Failed to connect GitHub', 'error');
    }
  };

  const handleGitHubConfigure = async () => {
    try {
      // Reuse the same install URL - GitHub will show existing installation for configuration
      // Pass skipReconnect=true to always go to GitHub
      const returnUrl = window.location.origin + window.location.pathname;
      const response = await integrationsApi.getGitHubInstallUrl(returnUrl, true);

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to get GitHub configure URL:', error);
      showToast('Failed to open GitHub configuration', 'error');
    }
  };

  const handleGitHubDisconnect = async () => {
    try {
      await integrationsApi.disconnectGitHub();
      setConnectedStatus('github', false);
      invalidateIntegrationStatuses();
      showToast('GitHub disconnected successfully', 'success');
    } catch (error) {
      console.error('Failed to disconnect GitHub:', error);
      showToast('Failed to disconnect GitHub', 'error');
      throw error; // Re-throw so dialog knows to stay open
    }
  };

  const handleJiraConnect = async () => {
    try {
      // Use clean URL without query params to avoid duplicates on redirect
      const returnUrl = window.location.origin + window.location.pathname;
      const { url } = await integrationsApi.getJiraConnectUrl(returnUrl);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to get Jira connect URL:', error);
      showToast('Failed to connect Jira', 'error');
    }
  };

  const handleJiraDisconnect = async () => {
    try {
      await integrationsApi.disconnectJira();
      setConnectedStatus('jira', false);
      invalidateIntegrationStatuses();
      showToast('Jira disconnected successfully', 'success');
    } catch (error) {
      console.error('Failed to disconnect Jira:', error);
      showToast('Failed to disconnect Jira', 'error');
      throw error; // Re-throw so dialog knows to stay open
    }
  };

  // Organization handlers (mock functionality for now)
  const handleSaveOrganizationName = () => {
    showToast('Organization name saved successfully!', 'success');
  };

  const closeAddMemberModal = (force = false) => {
    if (isAddingMember && !force) {
      return;
    }

    setShowAddMemberModal(false);
    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberRole('member');
    setAddMemberError(null);
  };

  const closeEditRoleModal = (force = false) => {
    if (isUpdatingRole && !force) {
      return;
    }

    setShowEditRoleModal(false);
    setEditingMember(null);
    setEditRoleValue('member');
    setEditRoleError(null);
  };

  const handleInviteUser = async () => {
    const name = newMemberName.trim();
    const email = newMemberEmail.trim().toLowerCase();

    if (!name) {
      setAddMemberError('Please enter a name.');
      return;
    }

    if (!email) {
      setAddMemberError('Please enter an email address.');
      return;
    }

    if (!EMAIL_PATTERN.test(email)) {
      setAddMemberError('Please enter a valid email address.');
      return;
    }

    setIsAddingMember(true);
    setAddMemberError(null);

    try {
      await usersApi.createTenantMember({
        email,
        name,
        role: newMemberRole,
      });

      await queryClient.invalidateQueries({ queryKey: TENANT_MEMBERS_QUERY_KEY });
      closeAddMemberModal(true);
      showToast('Member added successfully.', 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add member. Please try again.';
      setAddMemberError(message);
      showToast(message, 'error');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveUserClick = (userId: string, userName: string) => {
    setRemovingUser({ id: userId, name: userName });
    setShowRemoveUserConfirm(true);
  };

  const handleEditRoleClick = (member: {
    id: string;
    name: string | null;
    email: string;
    role: 'admin' | 'member';
  }) => {
    setEditingMember({
      id: member.id,
      name: getMemberDisplayName(member.name, member.email),
      role: member.role,
    });
    setEditRoleValue(member.role);
    setEditRoleError(null);
    setShowEditRoleModal(true);
  };

  const handleUpdateRole = async () => {
    if (!editingMember) {
      return;
    }

    setIsUpdatingRole(true);
    setEditRoleError(null);

    try {
      await usersApi.updateTenantMemberRole(editingMember.id, { role: editRoleValue });
      await queryClient.invalidateQueries({ queryKey: TENANT_MEMBERS_QUERY_KEY });
      closeEditRoleModal(true);
      showToast('Member role updated successfully.', 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update member role. Please try again.';
      setEditRoleError(message);
      showToast(message, 'error');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleRemoveUserConfirm = async () => {
    if (!removingUser) {
      return;
    }

    try {
      await usersApi.deleteTenantMember(removingUser.id);
      await queryClient.invalidateQueries({ queryKey: TENANT_MEMBERS_QUERY_KEY });
      setRemovingUser(null);
      showToast('Member removed successfully.', 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to remove member. Please try again.';
      showToast(message, 'error');
      throw error;
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <LoadingState
        message={isMembersLoading ? 'Loading organization...' : 'Loading settings...'}
        isLoading={isLoading || isMembersLoading}
      />
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Account & Organization</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Manage integrations, AI settings, and your organization
        </p>
      </div>

      {/* Appearance Card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Appearance</h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Theme</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Choose your preferred color theme
              </p>
            </div>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
            >
              {theme === 'light' ? (
                <>
                  <Sun className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Integrations Card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Integrations</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Connect your tools to enable AI bug resolution
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {/* GitHub Integration */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <Github className="w-6 h-6 text-gray-900 dark:text-white" />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white">GitHub</span>
                {githubStatus?.status === 'connected' ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-600" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Not Connected</span>
                  </div>
                )}
              </div>
            </div>
            {githubStatus?.status === 'connected' ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGitHubConfigure}
                  className="px-4 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
                >
                  Configure
                </button>
                <button
                  onClick={() => setShowGitHubDisconnectConfirm(true)}
                  className="px-4 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={handleGitHubConnect}
                className="px-4 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
              >
                Connect
              </button>
            )}
          </div>

          {/* Jira Integration */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <JiraLogo className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white">Jira</span>
                {jiraStatus?.status === 'connected' ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-600" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Not Connected</span>
                  </div>
                )}
              </div>
            </div>
            {jiraStatus?.status === 'connected' ? (
              <button
                onClick={() => setShowJiraDisconnectConfirm(true)}
                className="px-4 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleJiraConnect}
                className="px-4 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
              >
                Connect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Organization Card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Organization</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Manage your organization and team members
          </p>
        </div>

        {/* Organization Name Section */}
        <div className="px-6 py-4">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Organization Name</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              This name is visible to all team members
            </p>
          </div>
          <div className="flex gap-3 mt-3">
            <input
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
            <button
              onClick={handleSaveOrganizationName}
              className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl transition-colors"
            >
              Save
            </button>
          </div>
        </div>

        {/* Dotted Divider */}
        <div className="px-6">
          <div className="border-t border-dashed border-gray-300 dark:border-gray-600" />
        </div>

        {/* Team Members Section */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Team Members</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Manage who has access to your organization
              </p>
            </div>
            {isAdmin ? (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Add Member
              </button>
            ) : null}
          </div>

          {/* Team Members List */}
          <div className="space-y-3">
            {hasMembersError ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-sm text-red-700 dark:text-red-300">
                  Failed to load team members.
                </p>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400">No team members found.</p>
              </div>
            ) : (
              teamMembers.map((member) => {
                const displayName = getMemberDisplayName(member.name, member.email);

                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-medium text-sm">
                        {getInitials(member.name, member.email)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {displayName}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-lg border ${member.role === 'admin'
                            ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                          }`}
                      >
                        {member.role === 'admin' ? 'Admin' : 'Member'}
                      </span>
                      {isAdmin && member.id !== user?.id ? (
                        <>
                          <button
                            onClick={() => handleEditRoleClick(member)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                            aria-label={`Edit role for ${displayName}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                              onClick={() => handleRemoveUserClick(member.id, displayName)}
                              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              aria-label={`Remove ${displayName}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showGitHubDisconnectConfirm}
        onClose={() => setShowGitHubDisconnectConfirm(false)}
        onConfirm={handleGitHubDisconnect}
        title="Disconnect GitHub"
        message="Are you sure you want to disconnect GitHub? This will remove access to all connected repositories."
        confirmLabel="Disconnect"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showJiraDisconnectConfirm}
        onClose={() => setShowJiraDisconnectConfirm(false)}
        onConfirm={handleJiraDisconnect}
        title="Disconnect Jira"
        message="Are you sure you want to disconnect Jira? This will remove access to all connected projects."
        confirmLabel="Disconnect"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showRemoveUserConfirm}
        onClose={() => {
          setShowRemoveUserConfirm(false);
          setRemovingUser(null);
        }}
        onConfirm={handleRemoveUserConfirm}
        title="Remove User"
        message={`Are you sure you want to remove ${removingUser?.name} from the organization?`}
        confirmLabel="Remove"
        variant="danger"
      />

      <Modal
        isOpen={showEditRoleModal}
        onClose={() => closeEditRoleModal()}
        title="Edit Role"
        footer={
          <>
            <button
              onClick={() => closeEditRoleModal()}
              disabled={isUpdatingRole}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateRole}
              disabled={isUpdatingRole}
              className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingRole ? 'Saving...' : 'Save Role'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Update the organization role for {editingMember?.name ?? 'this member'}.
          </p>

          <div>
            <label
              htmlFor="edit-member-role"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Role
            </label>
            <select
              id="edit-member-role"
              value={editRoleValue}
              onChange={(e) => setEditRoleValue(e.target.value as 'admin' | 'member')}
              disabled={isUpdatingRole}
              className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {editRoleError ? (
            <p className="text-sm text-red-600 dark:text-red-400">{editRoleError}</p>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={showAddMemberModal}
        onClose={() => closeAddMemberModal()}
        title="Add Member"
        footer={
          <>
            <button
              onClick={() => closeAddMemberModal()}
              disabled={isAddingMember}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleInviteUser}
              disabled={isAddingMember}
              className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingMember ? 'Adding...' : 'Add Member'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Add a team member who can sign in with a magic link using their company email.
          </p>

          <div>
            <label
              htmlFor="new-member-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Name
            </label>
            <input
              id="new-member-name"
              type="text"
              value={newMemberName}
              onChange={(e) => {
                setNewMemberName(e.target.value);
                if (addMemberError) {
                  setAddMemberError(null);
                }
              }}
              placeholder="Teammate Name"
              disabled={isAddingMember}
              className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label
              htmlFor="new-member-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Email
            </label>
            <input
              id="new-member-email"
              type="email"
              value={newMemberEmail}
              onChange={(e) => {
                setNewMemberEmail(e.target.value);
                if (addMemberError) {
                  setAddMemberError(null);
                }
              }}
              placeholder="teammate@company.com"
              disabled={isAddingMember}
              className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label
              htmlFor="new-member-role"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Role
            </label>
            <select
              id="new-member-role"
              value={newMemberRole}
              onChange={(e) => setNewMemberRole(e.target.value as 'admin' | 'member')}
              disabled={isAddingMember}
              className="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {addMemberError ? (
            <p className="text-sm text-red-600 dark:text-red-400">{addMemberError}</p>
          ) : null}
        </div>
      </Modal>

      {/* Toast Notifications */}
      <Toast
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        message={toast.message}
        variant={toast.variant}
      />
    </div>
  );
}
